import { useQuery } from "@tanstack/react-query";
import type { JSX } from "react";
import { useState } from "react";
import { Link } from "react-router-dom";

import { apiRequest } from "../../common/api/apiClient.js";
import { formatWhen } from "../../common/components/formatWhen.js";
import { Money } from "../../common/components/Money.js";
import {
  SERVICE_TYPES,
  ServiceTypeBadge,
  serviceTypeClass,
  type ServiceType,
} from "../../common/components/ServiceTypeBadge.js";
import type { components } from "../../generated/api.js";

type ServiceDTO = components["schemas"]["ServiceDTO"];

const TYPE_ORDER: ServiceType[] = ["EVENT", "APPOINTMENT", "RENT"];

/** Der Feed: das veröffentlichte Angebot, filterbar nach Buchungsart. */
export function FeedPage(): JSX.Element {
  const [type, setType] = useState<ServiceType | null>(null);

  const offers = useQuery({
    queryKey: ["offers", type],
    queryFn: () =>
      apiRequest<ServiceDTO[]>(type ? `/offers?type=${type}` : "/offers", { tenant: null }),
  });

  return (
    <div className="mx-auto w-full max-w-3xl px-4 pb-6">
      <h1 className="pt-4 text-[22px] font-extrabold tracking-[-0.03em] text-ink">Entdecken</h1>

      {/* Buchungsart-Filter: aktiv färbt in die Typfarbe, nicht in die Marke. */}
      <div className="mt-4 flex flex-wrap gap-2">
        <button
          type="button"
          className={type === null ? "chip on" : "chip"}
          onClick={() => setType(null)}
        >
          Alle
        </button>
        {TYPE_ORDER.map((entry) => {
          const meta = SERVICE_TYPES[entry];
          const { Icon } = meta;
          return (
            <button
              key={entry}
              type="button"
              className={`${meta.className} ${type === entry ? "chip on tint" : "chip"}`}
              onClick={() => setType(entry)}
              aria-pressed={type === entry}
            >
              <Icon size={14} aria-hidden="true" />
              {meta.label}
            </button>
          );
        })}
      </div>

      {offers.isPending && <p className="mt-6 text-[14px] text-ink-3">Angebote werden geladen …</p>}

      {offers.isError && (
        <p className="mt-6 text-[14px] text-danger">
          Die Angebote sind gerade nicht erreichbar.
        </p>
      )}

      {offers.data?.length === 0 && (
        <p className="mt-6 text-[14px] text-ink-3">
          Hier ist noch nichts veröffentlicht. Sobald Anbieter ihren Katalog freigeben,
          erscheinen die Angebote an dieser Stelle.
        </p>
      )}

      <ul className="mt-5 flex flex-col gap-2.5">
        {offers.data?.map((offer) => (
          <li key={offer.id}>
            <Link
              to={`/angebot/${encodeURIComponent(offer.id)}`}
              className={`${serviceTypeClass(offer.type)} card hover pad flex items-start gap-3`}
            >
              <ServiceTypeBadge type={offer.type} square />
              <div className="min-w-0 flex-1">
                <p className="truncate text-[15px] font-bold tracking-[-0.015em] text-ink">
                  {offer.title}
                </p>
                {offer.description && (
                  <p className="mt-0.5 line-clamp-2 text-[13px] text-ink-2">{offer.description}</p>
                )}
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <ServiceTypeBadge type={offer.type} />
                  {offer.startsAt && (
                    <span className="num text-[12.5px] font-bold text-ink-2">
                      {formatWhen(offer.startsAt, offer.venueTimeZone)}
                    </span>
                  )}
                  {offer.capacity !== null && (
                    <span className="num text-[12.5px] font-bold text-ink-3">
                      {offer.capacity} Plätze
                    </span>
                  )}
                </div>
              </div>
              <span className="text-[15px] font-extrabold text-ink">
                <Money cents={offer.priceCents} currency={offer.currency} />
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
